<script setup lang="ts">
import { LEGAL_DOCUMENT_PATHS } from '@falasta/contracts';

import { useCookieConsent } from '../analytics/cookie-consent';
import { appUrl, appVersion } from '../config/runtime-config';
import { LEGAL_HOLDER } from '../content/legal-content';

/**
 * Piede: il solo simbolo (`brand/mark.svg`) accanto alla parola «Falasta»
 * scritta in HTML — il logo completo, che il lettering ce l'ha dentro, qui la
 * scriverebbe due volte.
 *
 * È anche il posto in cui i documenti legali devono essere raggiungibili da
 * **ogni** pagina, insieme alla revoca del consenso ai cookie: nasconderla in
 * una pagina sola equivarrebbe a non averla.
 */
const cookies = useCookieConsent();
</script>

<template>
  <footer class="foot">
    <div class="page foot__inner">
      <div class="foot__brand">
        <img src="/brand/mark.svg" width="36" height="25" alt="" />
        <div>
          <p class="foot__name">Falasta</p>
          <p class="foot__claim">Aste fantacalcio in tempo reale</p>
        </div>
      </div>

      <nav class="foot__links" aria-label="Collegamenti">
        <a :href="appUrl">Entra nell’asta</a>
        <a href="/#cosa-fa">Cosa fa</a>
        <a href="/#come-funziona">Come funziona</a>
        <a href="/#domande">Domande</a>
      </nav>

      <p class="foot__version">v{{ appVersion }}</p>

      <nav class="foot__legal" aria-label="Informazioni legali">
        <a :href="LEGAL_DOCUMENT_PATHS.privacy">Privacy</a>
        <a :href="LEGAL_DOCUMENT_PATHS.terms">Termini</a>
        <a :href="LEGAL_DOCUMENT_PATHS.cookies">Cookie</a>
        <button type="button" class="foot__button" @click="cookies.reopen()">
          Preferenze cookie
        </button>
        <a :href="`mailto:${LEGAL_HOLDER.email}`">Contatti</a>
      </nav>
    </div>
  </footer>
</template>

<style scoped>
.foot {
  border-top: 1px solid var(--color-border);
  padding-block: 2rem;
  background: var(--color-surface);
}

.foot__inner {
  display: grid;
  gap: 1.25rem;
  align-items: center;
}

.foot__brand {
  display: flex;
  align-items: center;
  gap: 0.7rem;
}

.foot__brand img {
  display: block;
  height: 1.6rem;
  width: auto;
}

.foot__name {
  color: var(--color-heading);
  font-weight: 800;
  letter-spacing: -0.01em;
}

.foot__claim {
  color: var(--color-faint);
  font-size: 0.8125rem;
}

.foot__links {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem 1.25rem;
  font-size: 0.875rem;
}

.foot__links a {
  color: var(--color-muted);
  text-decoration: none;
}

.foot__links a:hover {
  color: var(--color-heading);
}

.foot__version {
  color: var(--color-faint);
  font-size: 0.8125rem;
  font-variant-numeric: tabular-nums;
}

/* Riga a sé, sotto una linea: sono i documenti, non la navigazione del sito. */
.foot__legal {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1.25rem;
  padding-top: 1rem;
  border-top: 1px solid var(--color-border);
  font-size: 0.8125rem;
}

.foot__legal a,
.foot__button {
  color: var(--color-faint);
  text-decoration: none;
}

.foot__button {
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  cursor: pointer;
}

.foot__legal a:hover,
.foot__button:hover {
  color: var(--color-heading);
  text-decoration: underline;
}

@media (min-width: 48rem) {
  .foot__inner {
    grid-template-columns: auto 1fr auto;
    gap: 1.25rem 2rem;
  }

  .foot__links {
    justify-content: center;
  }

  /* La riga legale sta sotto le altre tre, per tutta la larghezza. */
  .foot__legal {
    grid-column: 1 / -1;
  }
}
</style>
